import React, { useState, useEffect } from 'react';
import { Button, SvgIcon, Box, keyframes, styled } from '@mui/material';
import useAppStore from '../../store/rootStore';
import { telemetry } from '../../utils/telemetry';
import { RaisedHandIcon } from './raised_hand';
import wikSmall from '@/assets/wik-feedback.png';
import wikBig from '@/assets/wik-feedback-big.png';
import { DEFAULT_TOAST_DURATION, TOAST_DURATION_OPTIONS } from '@/common/timeout_constants.ts';
import useAuthStore from '../../store/rootStore';
import { useDialogStore } from '@/components/dialogs';
import { useNotificationStore } from '@/feature/notifications';
import { TournamentDialog } from './TournamentDialog.tsx';

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const FeedbackContainer = styled(Box)({
  position: 'relative',
  alignSelf: 'flex-end',
});

const buttonSx = {
  position: 'relative',
  zIndex: 2,
};

const WikImage = styled('img')<{ isVisible: boolean; isHiding: boolean }>(({ theme, isVisible, isHiding }) => ({
  display: isVisible ? 'block' : 'none',
  position: 'absolute',
  width: '192px',
  height: 'auto',
  bottom: 0,
  right: 40,
  content: `url(${wikSmall})`,
  animation: isHiding ? `${slideDown} 0.5s ease-in forwards` : `${slideUp} 0.8s ease-out`,
  zIndex: 3,
  pointerEvents: 'none',
  [theme.breakpoints.up('sm')]: {
    content: `url(${wikBig})`,
  },
}));

export function useFeedbackEnabled() {
  const isAuthenticated = useAppStore((state) => state.isAtLeastAuthenticated());
  const collectUsersFeedback = useAppStore((state) => state.config?.features?.collectUsersFeedback ?? false);
  return isAuthenticated && collectUsersFeedback && telemetry.isLoaded();
}

/**
 * PostHog-based feedback button. Triggers PostHog surveys for user feedback collection.
 */
export const FeedbackButton: React.FC = () => {
  const isAuthenticated = useAppStore((state) => state.isAtLeastAuthenticated());
  const feedbackEnabled = useFeedbackEnabled();
  const isAuthorized = useAppStore((state) => state.isFullyAuthorized());
  const openDialogs = useDialogStore((state) => state.openDialogs);
  const openDialog = useDialogStore((state) => state.openDialog);
  const toastDurationPreference = useNotificationStore((state) => state.toastDuration);
  const hasSeenLeaderboardDialog = useAppStore((state) =>
    state.address ? !!state.termsAccepted[state.address]?.tournamentDialogSeen : false
  );
  const setLeaderboardDialogSeen = useAppStore((state) => state.setLeaderboardDialogSeen);
  const [isVisible, setIsVisible] = useState(false);
  const [isHiding, setIsHiding] = useState(false);
  const hasBlockingDialogOpen = Object.entries(openDialogs).some(([key, value]) => key !== 'tournamentDialog' && value);

  useEffect(() => {
    const { hasShownWikImage, setWikImageShown } = useAuthStore.getState();

    if (isAuthenticated && telemetry.isLoaded() && !hasShownWikImage) {
      setWikImageShown();
      setIsVisible(true);
      setIsHiding(false);

      const preferredDuration =
        TOAST_DURATION_OPTIONS[toastDurationPreference] ?? TOAST_DURATION_OPTIONS[DEFAULT_TOAST_DURATION];
      const fadeOutBuffer = 500;

      const hideTimer = setTimeout(() => {
        setIsHiding(true);
      }, preferredDuration);

      const removeTimer = setTimeout(() => {
        setIsVisible(false);
        setIsHiding(false);
      }, preferredDuration + fadeOutBuffer);

      return () => {
        clearTimeout(hideTimer);
        clearTimeout(removeTimer);
      };
    }
  }, [isAuthenticated, toastDurationPreference]);

  // useEffect(() => {
  //   if (!isAuthorized || hasSeenLeaderboardDialog || hasBlockingDialogOpen) {
  //     return;
  //   }
  //
  //   openDialog('tournamentDialog');
  //   setLeaderboardDialogSeen();
  // }, [hasBlockingDialogOpen, hasSeenLeaderboardDialog, isAuthorized, openDialog, setLeaderboardDialogSeen]);

  if (!feedbackEnabled) {
    return null;
  }

  const handleOpenFeedback = () => {
    telemetry.logEvent('feedback_button_clicked');
  };

  return (
    <>
      <FeedbackContainer>
        <Button
          color="info"
          size="large"
          variant="contained"
          aria-label="feedback"
          onClick={handleOpenFeedback}
          sx={buttonSx}
          startIcon={
            <SvgIcon>
              <RaisedHandIcon />
            </SvgIcon>
          }
        >
          Feedback
        </Button>
        <WikImage src={wikSmall} alt="Wik" isVisible={isVisible} isHiding={isHiding} />
      </FeedbackContainer>
      <TournamentDialog />
    </>
  );
};
